import { selectRole, selectToken, selectUserUuid } from '@redux/slices/userSlice';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { coreService } from 'network-utils';
import { useMutation, useQueryClient } from 'react-query';
import { getQuizInfo, useGetQuizInfo } from '@queries/core/quiz';
import { NotFound } from 'learning-panel-components';
import { getHomepage } from '@services/getHomepage';
import { useGetUserProfilePicture } from '@queries/platform/aws';
import { showAlert } from '@redux/slices/informationSlice';
import QuizQuestionsPlayer from './quizQuestionsPlayer';
import QuizPlayer from './quizPlayer';

type QuizState = 'init' | 'player';
const QuizContainer: FC = () => {
    const token = useSelector(selectToken);
    const role = useSelector(selectRole);
    const services = coreService.quizApi;
    const [quizInfo, setQuizInfo] = useState<coreService.QuizInfo>();
    const [quizState, setQuizState] = useState<QuizState>('init');
    const [quizQuestion, setQuizQuestion] = useState<coreService.QuizQuestion>();
    const dispatch = useDispatch();
    const locationHook = useLocation();
    const queryClient = useQueryClient();
    const queryParams = new URLSearchParams(locationHook.search);
    const id = queryParams.get('quiz');
    const userUuid = useSelector(selectUserUuid);

    const { data: quizInfoData, isLoading } = useGetQuizInfo(token, Number(id) || 0);
    const { data: userProfilePicture } = useGetUserProfilePicture(userUuid);

    const handleRetakeTest = () => {
        setQuizState('init');
        queryClient.invalidateQueries(getQuizInfo);
    };

    useEffect(() => {
        if (quizInfoData) {
            setQuizInfo(quizInfoData);
        }
    }, [quizInfoData]);

    const onStart = (quizId: number): void => {
        startMutation({
            quizId
        });
    };

    const start = async (input: {
        quizId: number;
    }): Promise<{
        success: boolean;
        questionId: number;
        numberOfQuestions: number;
        quizProgressId: number;
        error?: string;
    }> => {
        const result = await services.start(token, input);
        return result;
    };

    const { mutate: startMutation } = useMutation(start, {
        onSuccess: (result: {
            success: boolean;
            questionId: number;
            numberOfQuestions: number;
            quizProgressId: number;
            error?: string;
        }) => {
            if (result.success) {
                setQuizQuestion({
                    questionId: result.questionId,
                    numberOfQuestions: result.numberOfQuestions,
                    quizProgressId: result.quizProgressId
                });
                setQuizState('player');
            }
            if (!result.success) {
                if (result.error === 'quiz_attempts_exceeded') {
                    dispatch(
                        showAlert({
                            value: 'AllAttemptsUsed',
                            status: 'error',
                            title: '',
                            isAdmin: true
                        })
                    );
                } else if (result.error === 'quiz_already_completed') {
                    dispatch(
                        showAlert({
                            value: 'quizAlreadyCompleted',
                            status: 'error',
                            title: '',
                            isAdmin: true
                        })
                    );
                } else {
                    dispatch(
                        showAlert({
                            value: 'FailedToStartQuiz',
                            status: 'error',
                            title: '',
                            isAdmin: true
                        })
                    );
                }
            }
        },
        onError: () => {
            dispatch(
                showAlert({
                    value: 'FailedToStartQuiz',
                    status: 'error',
                    title: '',
                    isAdmin: true
                })
            );
        }
    });

    const renderContent = () => {
        if (!quizInfo && !isLoading) {
            return (
                <NotFound
                    onGoHome={() => {
                        if (window.top) {
                            window.top.location.href = getHomepage(role);
                        }
                    }}
                />
            );
        }

        if (quizState === 'init' && quizInfo) {
            return <QuizPlayer quizInfo={quizInfo} startTest={() => onStart(Number(id))} />;
        }

        if (quizState === 'player' && quizQuestion && quizInfo) {
            return (
                <QuizQuestionsPlayer
                    name={quizInfo.courseDisplayName}
                    isRetakeEnabled={quizInfo.attemptsAllowed > 0}
                    quizQuestion={quizQuestion}
                    setQuizQuestion={setQuizQuestion}
                    quizInfo={quizInfo}
                    onRetake={handleRetakeTest}
                    userAvatar={userProfilePicture?.url}
                />
            );
        }

        return <div></div>;
    };

    return <div>{renderContent()}</div>;
};

interface QuizContainerProps {
    quizId: number;
}

export default QuizContainer;
